import React, { useState } from "react";
import { useGetPlatformUsersQuery, useLoginAsUserMutation } from "@/store/admin_api";
import type User from "@/model/interface/User";
import { authService } from "@/services/authService";
import { apiErrorMessage } from "@/util/apiError";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Input } from "@/components/ui/input";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { FaEllipsisH, FaSignInAlt } from "react-icons/fa";
import { toast } from "sonner";

const ROLE_BADGE_VARIANTS: Record<string, "default" | "secondary" | "destructive" | "outline"> = {
  PLATFORM_ADMIN: "destructive",
  ADMIN: "default",
  USER: "secondary",
  GUEST: "outline",
};

export default function UsersList() {
  const { data: users, isLoading } = useGetPlatformUsersQuery();
  const [loginAsUser, { isLoading: isLoggingIn }] = useLoginAsUserMutation();
  const [search, setSearch] = useState("");
  const [loginTarget, setLoginTarget] = useState<User | null>(null);

  const userName = (user: User) => {
    const name = [user.firstName, user.lastName].filter(Boolean).join(" ");
    return name || "-";
  };

  const filteredUsers = users?.filter((user) => {
    const term = search.trim().toLowerCase();
    if (!term) {
      return true;
    }
    return [userName(user), user.email, user.activeOrganisation?.name]
      .filter(Boolean)
      .some((value) => value!.toLowerCase().includes(term));
  });

  const handleLoginAs = async (user: User) => {
    try {
      const adminAccessToken = await authService.getAccessToken();
      const adminRefreshToken = await authService.getRefreshToken();
      if (!adminAccessToken || !adminRefreshToken) {
        toast.error("Your own session tokens are missing");
        return;
      }
      const session = await loginAsUser(user.id!).unwrap();
      if (!session.accessToken || !session.refreshToken) {
        toast.error("The session tokens were missing from the response");
        return;
      }
      await authService.setAccessToken(session.accessToken);
      await authService.setRefreshToken(session.refreshToken);
      const label = userName(user) !== "-" ? userName(user) : user.email;
      await authService.beginImpersonation(adminAccessToken, adminRefreshToken, label);
      window.location.href = "/";
    } catch (error) {
      toast.error(apiErrorMessage(error, "Failed to log in as this user"));
    } finally {
      setLoginTarget(null);
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-2xl font-bold">Users</h2>
        <p className="text-muted-foreground text-sm">
          Every user on the platform. Log in as a user to see the app exactly as they do.
        </p>
      </div>

      <Input
        placeholder="Search by name, email or organisation"
        value={search}
        onChange={(event) => setSearch(event.target.value)}
        className="max-w-sm"
      />

      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Name</TableHead>
            <TableHead>Email</TableHead>
            <TableHead>Organisation</TableHead>
            <TableHead>Role</TableHead>
            <TableHead>2FA</TableHead>
            <TableHead>Joined</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {!filteredUsers?.length && (
            <TableRow>
              <TableCell colSpan={7} className="text-center text-muted-foreground py-8">
                No users found
              </TableCell>
            </TableRow>
          )}
          {filteredUsers?.map((user) => (
            <TableRow key={user.id}>
              <TableCell className="font-medium">{userName(user)}</TableCell>
              <TableCell>{user.email}</TableCell>
              <TableCell>{user.activeOrganisation?.name || "-"}</TableCell>
              <TableCell>
                <Badge variant={ROLE_BADGE_VARIANTS[user.role ?? ""] || "outline"}>
                  {user.role ?? "-"}
                </Badge>
              </TableCell>
              <TableCell>
                {user.twoFactorMethod ? (
                  <Badge variant="secondary">{user.twoFactorMethod}</Badge>
                ) : (
                  <span className="text-muted-foreground">-</span>
                )}
              </TableCell>
              <TableCell>
                {user.createdAt ? new Date(user.createdAt).toLocaleDateString() : "-"}
              </TableCell>
              <TableCell className="text-right whitespace-nowrap">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button size="sm" variant="ghost">
                      <FaEllipsisH className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => setLoginTarget(user)}>
                      <FaSignInAlt className="mr-2 h-4 w-4" />
                      Log in as
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <AlertDialog open={loginTarget !== null} onOpenChange={(open) => !open && setLoginTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Log in as {loginTarget ? userName(loginTarget) : ""}?</AlertDialogTitle>
            <AlertDialogDescription>
              You will start browsing as {loginTarget?.email}. You can switch back to your own
              account at any time from the bar at the top of the page.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={isLoggingIn}
              onClick={() => loginTarget && handleLoginAs(loginTarget)}
            >
              Log in as
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
